import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, Upload } from "lucide-react";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { Button } from "../../../components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/dialog";
import { validateImportEvents } from "../api/event.api";
import { useImportEvents } from "../hooks";
import type { EventImportResponse, EventImportReviewRow } from "../types/event.types";

interface EventImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (result: EventImportResponse) => Promise<void> | void;
}

const columns = [
  "title",
  "description",
  "location",
  "latitude",
  "longitude",
  "radius",
  "event_date",
  "start_time",
  "end_time",
  "check_in_time",
  "image_url",
  "guest_attendance_verification",
  "timezone",
  "category_id",
  "sub_category_ids",
  "points",
  "neighborhood_id",
] as const;
const asText = (value: unknown) => String(value ?? "").trim();
const asNumber = (value: unknown) => {
  const text = asText(value);
  return text ? Number(text) : null;
};
const asIds = (value: unknown) =>
  asText(value)
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

function reviewWorkbook(file: File, buffer: ArrayBuffer): EventImportReviewRow[] {
  if (!file.name.toLowerCase().endsWith(".xlsx")) throw new Error("Choose an .xlsx file.");
  const workbook = XLSX.read(buffer, { type: "array" });
  if (workbook.SheetNames.length !== 1 || workbook.SheetNames[0] !== "Events")
    throw new Error("The workbook must contain only an Events sheet.");
  const values = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets.Events, {
    header: 1,
    defval: "",
  });
  const headers = (values[0] ?? []).map(asText);
  const missing = columns.filter((column) => !headers.includes(column));
  if (missing.length) throw new Error(`Events is missing columns: ${missing.join(", ")}.`);
  const columnIndex = (name: string) => headers.indexOf(name);
  const rows = values.slice(1).flatMap((values, index) => {
    if (!values.some((value) => asText(value))) return [];
    const get = (column: (typeof columns)[number]) => values[columnIndex(column)];
    const latitude = asNumber(get("latitude"));
    const longitude = asNumber(get("longitude"));
    const radius = asNumber(get("radius"));
    const points = asNumber(get("points"));
    const row: EventImportReviewRow = {
      row_number: index + 2,
      title: asText(get("title")),
      description: asText(get("description")),
      location: asText(get("location")),
      latitude,
      longitude,
      radius: radius ?? Number.NaN,
      event_date: asText(get("event_date")),
      start_time: asText(get("start_time")),
      end_time: asText(get("end_time")),
      check_in_time: asText(get("check_in_time")) || undefined,
      image_url: asText(get("image_url")),
      guest_attendance_verification: asText(get("guest_attendance_verification")) || undefined,
      timezone: asText(get("timezone")),
      category_id: asText(get("category_id")),
      sub_category_ids: asIds(get("sub_category_ids")),
      points,
      neighborhood_id: asText(get("neighborhood_id")),
      errors: [],
    };
    row.errors.push(
      ...(row.title ? [] : ["Title is required."]),
      ...(row.description ? [] : ["Description is required."]),
      ...(row.location ? [] : ["Location is required."]),
      ...(Number.isFinite(latitude) ? [] : ["Latitude must be a number."]),
      ...(Number.isFinite(longitude) ? [] : ["Longitude must be a number."]),
      ...(Number.isFinite(radius) && radius! > 0 ? [] : ["Radius must be greater than zero."]),
      ...(row.event_date ? [] : ["Event date is required."]),
      ...(row.start_time ? [] : ["Start time is required."]),
      ...(row.end_time ? [] : ["End time is required."]),
      ...(row.image_url ? [] : ["Image URL is required."]),
      ...(row.timezone ? [] : ["Timezone is required."]),
      ...(row.category_id ? [] : ["Category ID is required."]),
      ...(Number.isFinite(points) && points! > 0 ? [] : ["Points must be greater than zero."]),
      ...(row.neighborhood_id ? [] : ["Neighborhood ID is required."]),
    );
    return [row];
  });
  if (!rows.length) throw new Error("The Events sheet must contain at least one event.");
  return rows;
}

function toRequestRow({ errors: _errors, ...row }: EventImportReviewRow) {
  return row;
}

export function EventImportDialog({ open, onOpenChange, onImported }: EventImportDialogProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<EventImportReviewRow[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [success, setSuccess] = useState<EventImportResponse | null>(null);
  const { importEvents, loading, error, reset } = useImportEvents();
  const invalidCount = rows.filter((row) => row.errors.length > 0).length;
  const close = () => {
    if (loading) return;
    setFileName(null);
    setRows([]);
    setFileError(null);
    setSuccess(null);
    reset();
    onOpenChange(false);
  };
  const downloadTemplate = () => {
    const sheet = XLSX.utils.aoa_to_sheet([
      [...columns],
      [
        "Community cleanup",
        "Volunteer cleanup for the neighborhood.",
        "123 Main Street",
        10.7769,
        106.7009,
        100,
        "2099-06-15T00:00:00+07:00",
        "2099-06-15T08:00:00+07:00",
        "2099-06-15T11:00:00+07:00",
        "2099-06-15T08:00:00+07:00",
        "https://example.com/event-image.jpg",
        "required",
        "Asia/Ho_Chi_Minh",
        "REPLACE_WITH_CATEGORY_UUID",
        "REPLACE_WITH_SUB_CATEGORY_UUID;OPTIONAL_SECOND_SUB_CATEGORY_UUID",
        100,
        "REPLACE_WITH_NEIGHBORHOOD_UUID",
      ],
    ]);
    sheet["!cols"] = columns.map((column) => ({ wch: Math.max(column.length + 2, 18) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Events");
    XLSX.writeFile(workbook, "event-import-template.xlsx");
  };
  const selectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    reset();
    setSuccess(null);
    if (!file) return;
    try {
      const reviewedRows = reviewWorkbook(file, await file.arrayBuffer());
      const validation = await validateImportEvents(reviewedRows.map(toRequestRow));
      const errorsByRow = new Map(
        validation.events.map((item) => [
          item.row_number,
          item.errors.map((validationError) => validationError.message),
        ]),
      );
      setRows(
        reviewedRows.map((row) => ({
          ...row,
          errors: [...row.errors, ...(errorsByRow.get(row.row_number) ?? [])],
        })),
      );
      setFileName(file.name);
      setFileError(null);
    } catch (reason) {
      setRows([]);
      setFileName(null);
      setFileError(reason instanceof Error ? reason.message : "Unable to review this workbook.");
    }
  };
  const submit = async () => {
    if (!rows.length || invalidCount || loading) return;
    try {
      const result = await importEvents(rows.map(toRequestRow));
      await onImported(result);
      setSuccess(result);
    } catch (reason) {
      setFileError(reason instanceof Error ? reason.message : t("event.import.refresh_failed"));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : close())}
    >
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("event.import.title")}</DialogTitle>
          <DialogDescription>{t("event.import.description")}</DialogDescription>
        </DialogHeader>
        {success ? (
          <div className="rounded-xl border border-[#B7E1CD] bg-[#F3FBF6] p-4 text-[#0B6B45]">
            <CheckCircle2 className="mr-2 inline h-5 w-5" />
            {t("event.import.success", { count: success.eventsCreated })}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="brand-outline"
                onClick={downloadTemplate}
                disabled={loading}
              >
                <Download className="h-4 w-4" />
                {t("event.import.download_template")}
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                className="sr-only"
                onChange={(event) => void selectFile(event)}
                disabled={loading}
              />
              <Button
                variant="brand-secondary"
                onClick={() => inputRef.current?.click()}
                disabled={loading}
              >
                <Upload className="h-4 w-4" />
                {t("event.import.choose_file")}
              </Button>
            </div>
            <p className="text-sm text-[#52606D]">{t("event.import.template_hint")}</p>
            {rows.length ? (
              <div className="overflow-hidden rounded-xl border border-[#D5DDE7]">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#D5DDE7] bg-[#F8FAFC] px-4 py-3 text-sm">
                  <span className="font-medium text-[#0F172A]">{fileName}</span>
                  <span className={invalidCount ? "text-[#B42318]" : "text-[#0B6B45]"}>
                    {t("event.import.review_summary", {
                      total: rows.length,
                      invalid: invalidCount,
                    })}
                  </span>
                </div>
                <div className="max-h-80 overflow-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="sticky top-0 bg-white text-xs font-semibold uppercase tracking-wide text-[#52606D]">
                      <tr>
                        <th className="px-4 py-3">{t("event.import.row")}</th>
                        <th className="px-4 py-3">{t("event.title")}</th>
                        <th className="px-4 py-3">{t("event.date")}</th>
                        <th className="px-4 py-3">{t("event.import.status")}</th>
                        <th className="px-4 py-3">{t("event.import.action")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {rows.map((row) => (
                        <tr
                          key={row.row_number}
                          className={row.errors.length ? "bg-[#FFF6F6]" : "bg-white"}
                        >
                          <td className="px-4 py-3 text-[#52606D]">{row.row_number}</td>
                          <td className="px-4 py-3">{row.title || "—"}</td>
                          <td className="px-4 py-3">{row.start_time || row.event_date || "—"}</td>
                          <td className="px-4 py-3">
                            {row.errors.length ? (
                              <span className="text-[#B42318]">{row.errors.join(" ")}</span>
                            ) : (
                              <span className="text-[#0B6B45]">{t("event.import.valid")}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() =>
                                setRows((currentRows) =>
                                  currentRows.filter((item) => item.row_number !== row.row_number),
                                )
                              }
                              className="text-sm font-medium text-[#B42318] hover:underline"
                            >
                              {t("event.import.delete_row")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
            {fileError || error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mr-2 inline h-4 w-4" />
                {fileError ?? error}
              </div>
            ) : null}
          </div>
        )}
        <DialogFooter>
          <Button
            variant="brand-outline"
            onClick={close}
            disabled={loading}
          >
            {success ? t("event.import.done") : t("common.cancel")}
          </Button>
          {!success ? (
            <Button
              variant="brand-secondary"
              onClick={() => void submit()}
              disabled={!rows.length || invalidCount > 0 || loading}
            >
              <FileSpreadsheet className="h-4 w-4" />
              {loading ? t("event.import.importing") : t("event.import.submit")}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
